/**
 * The choices the candidate form offers, in the order it offers them.
 *
 * All in one module because three screens read them and they have to agree: the
 * edit dialog writes a value, the table prints it, and the detail panel prints
 * it again. A second copy of the notice-period list is how a row comes to read
 * "90 days" under a dropdown that no longer offers ninety.
 *
 * **Every list here is a presentation choice, not a rule.** The columns behind
 * them are unconstrained — `qualification` is varchar(160), `current_salary` is
 * numeric, `notice_period_days` is an integer — and stay that way deliberately.
 * The importer carries 346,067 rows of free text that predate every one of
 * these lists, and a column that refused them would refuse the data this
 * business actually has. So each list is offered, and anything already stored
 * that falls outside it survives being shown and saved: see `withStored`.
 */
import { rupeesWhole } from './money';

export interface Choice {
  /** What goes in the column. */
  value: string;
  /** What a person reads. */
  label: string;
}

/**
 * The stored value as its own option, where the list does not already offer it.
 *
 * The whole reason the dropdowns are safe to introduce. A candidate imported
 * with "B.Sc Nursing (Distance)" or a notice of fourteen days holds a value no
 * list contains, and a select whose value is absent from its options renders as
 * blank — so opening the dialog to fix a mobile number and pressing Save would
 * erase a qualification nobody touched. Offering it keeps the form honest about
 * what is stored, and lets somebody replace it deliberately rather than by
 * accident.
 *
 * Appended rather than sorted in: it is the odd one out, and putting it at the
 * end says so.
 */
export function withStored(choices: Choice[], stored: string | null | undefined): Choice[] {
  const value = (stored ?? '').trim();
  if (!value || choices.some((c) => c.value === value)) return choices;
  return [...choices, { value, label: value }];
}

/**
 * Qualifications, from school to doctorate.
 *
 * Broad on purpose: this business staffs hospitals, labs and back offices from
 * the same bench, so a list that covered only nursing would send half the
 * recruiters straight to the free-text fallback and the list would be worth
 * nothing. Ordered by level rather than alphabetically — somebody scanning for
 * "B.Sc" is scanning for where degrees start, and A-to-Z interleaves a
 * doctorate with a diploma.
 *
 * Stored as this exact label, which is why the spellings matter: the column
 * holds the string, and "B.Com" and "Bcom" are two qualifications to anybody
 * grouping them later.
 */
export interface QualificationChoice extends Choice {
  group: QualificationGroup;
}

export type QualificationGroup =
  | 'School & Diploma'
  | 'Nursing'
  | 'Pharmacy'
  | 'Medical & Allied'
  | 'Degree'
  | 'Postgraduate'
  | 'Other';

/**
 * The groups, in the order the form renders them.
 *
 * Thirty-six options in one flat column is a scroll rather than a choice --
 * the list is long enough that somebody looking for GNM reads past twenty
 * degrees to find it. Grouped by field, which is how a recruiter staffing a
 * hospital thinks about it: they are looking for a nursing qualification, and
 * the four of those sit together under a heading they can jump to.
 */
export const QUALIFICATION_GROUPS: QualificationGroup[] = [
  'School & Diploma',
  'Nursing',
  'Pharmacy',
  'Medical & Allied',
  'Degree',
  'Postgraduate',
  'Other',
];

const BY_GROUP: Record<QualificationGroup, string[]> = {
  'School & Diploma': ['10th', '12th', 'ITI', 'Diploma'],
  Nursing: ['ANM', 'GNM', 'B.Sc Nursing', 'Post Basic B.Sc Nursing', 'M.Sc Nursing'],
  Pharmacy: ['D.Pharm', 'B.Pharm', 'M.Pharm'],
  'Medical & Allied': ['BPT', 'MPT', 'BAMS', 'BHMS', 'BDS', 'MBBS', 'MD', 'MS'],
  Degree: [
    'B.A', 'B.Com', 'B.Sc', 'BBA', 'BCA', 'B.E / B.Tech', 'LLB',
  ],
  Postgraduate: [
    'M.A', 'M.Com', 'M.Sc', 'MBA', 'MCA', 'M.E / M.Tech', 'LLM', 'PhD',
  ],
  Other: ['Other'],
};

export const QUALIFICATIONS: QualificationChoice[] = QUALIFICATION_GROUPS.flatMap(
  (group) => BY_GROUP[group].map((name) => ({ value: name, label: name, group })),
);

/**
 * Take-home salary per month, as a ladder of bands.
 *
 * The range is the client's: ten thousand to two lakh. The *step* is not, and
 * it is the one decision here worth explaining. A flat 5,000 step would make
 * 38,000 — a real figure in this data — unselectable, so a recruiter would
 * round it to 40,000 and the candidate's pay would be overstated by two
 * thousand rupees in the record the client later negotiates against. So the
 * step follows where the figures actually sit: every thousand through the band
 * most take-home pay falls in, coarsening as the amounts grow and the
 * precision stops mattering.
 *
 *   10,000 – 50,000   every 1,000
 *   50,000 – 1,00,000 every 5,000
 *   1,00,000 – 2,00,000 every 10,000
 *
 * Sixty-one options is a long select and a short scroll; a wrong salary is
 * neither.
 */
function ladder(from: number, to: number, step: number): number[] {
  const out: number[] = [];
  for (let value = from; value <= to; value += step) out.push(value);
  return out;
}

export const SALARY_AMOUNTS: number[] = [
  ...ladder(10_000, 49_000, 1_000),
  ...ladder(50_000, 95_000, 5_000),
  ...ladder(100_000, 200_000, 10_000),
];

/**
 * The bands as a form's options.
 *
 * The value is a plain integer string because that is what the API takes for a
 * numeric column — `Decimal | None`, which accepts "35000" and would also
 * accept "₹35,000" as nothing at all. The label is what a person reads.
 *
 * Read by the two salary boxes through `SearchSelect`, which filters them as
 * somebody types — sixty-one options is a scroll nobody can aim at, and a
 * native panel that long opens outside the dialog it belongs to. The bands
 * stay a suggestion there rather than a rule: a real 38,500 is kept as typed.
 */
export const SALARY_BANDS: Choice[] = SALARY_AMOUNTS.map((amount) => ({
  value: String(amount),
  label: rupeesWhole(amount),
}));

/**
 * A stored salary as the form's own spelling of it.
 *
 * The API sends a decimal string — "35000.00" — and the options hold "35000",
 * so a stored salary would never match its own band and would be appended as a
 * stray extra option reading "35000.00". Trimming the paise where there are
 * none to lose is what makes the select find it.
 *
 * A figure with real paise keeps them and becomes its own option, which is the
 * right answer: 35,000.50 is not the 35,000 band.
 */
export function salaryFormValue(stored: string | null | undefined): string {
  const value = (stored ?? '').trim();
  if (!value) return '';
  const [whole, fraction] = value.split('.');
  return !fraction || /^0*$/.test(fraction) ? whole : value;
}

/**
 * Notice periods, and the days each one stores.
 *
 * The client's six buckets. Each stores the **upper** bound of its range, which
 * is the existing rule and not a new one: `notice_period_days` is what the
 * "available within N days" filter compares against, and a filter cannot read a
 * label. Storing the lower bound would promise a candidate is free sooner than
 * they said.
 *
 * Immediate is zero, which is the honest number for it and reads correctly
 * through that same filter — available within any horizon at all.
 *
 * Two of the three buckets this replaces keep their number, so no stored row
 * changes meaning: 30 was "15-30 days" and still is, 90 was "30-90 days" and is
 * now the top of "60-90 Days". The old fortnight bucket stored 14, which no
 * bucket claims now — those rows keep reading "14 days" through `withStored`
 * rather than being rounded into a range nobody chose.
 */
export const NOTICE_PERIODS: { label: string; days: number }[] = [
  { label: 'Immediate', days: 0 },
  { label: '7 Days to 15 Days', days: 15 },
  { label: '15 Days to 30 Days', days: 30 },
  { label: '30 Days to 45 Days', days: 45 },
  { label: '45 Days to 60 Days', days: 60 },
  { label: '60 Days to 90 Days', days: 90 },
];

/** The bucket a stored number falls in, or its own plain wording. */
export function noticeLabel(days: number | null | undefined): string | null {
  if (days === null || days === undefined) return null;
  const bucket = NOTICE_PERIODS.find((option) => option.days === days);
  // A value from before the buckets, or from an import, keeps its own wording
  // rather than being shown as a range it was never in.
  return bucket ? bucket.label : `${days} days`;
}

/**
 * Total experience as the two numbers people say out loud.
 *
 * "Three years six months", not "forty-two months" — and the column stores
 * months, so the form does the arithmetic. Thirty years of options rather than
 * sixty: the column allows 720 months, and a fortieth year on the list is an
 * option nobody picks below a scroll everybody pays for.
 */
export const EXPERIENCE_YEARS: number[] = ladder(0, 30, 1);
export const EXPERIENCE_MONTHS: number[] = ladder(0, 11, 1);

/** Years and months from the stored total. */
export function experienceParts(
  months: number | null | undefined,
): { years: string; months: string } {
  if (months === null || months === undefined) return { years: '', months: '' };
  return { years: String(Math.floor(months / 12)), months: String(months % 12) };
}

/**
 * The stored total from the two boxes.
 *
 * Both blank is null — "nobody asked" — and not zero, which is the real and
 * different claim that somebody is a fresher. Either one filled makes the other
 * zero, so picking "6 months" alone stores six rather than nothing.
 */
export function experienceMonthsFrom(years: string, months: string): number | null {
  if (years.trim() === '' && months.trim() === '') return null;
  const y = Number(years || 0);
  const m = Number(months || 0);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return null;
  return y * 12 + m;
}

/**
 * What a present salary can be quoted net of.
 *
 * The client's list. Each is a column on the candidate, so the label and the
 * field travel together — a checkbox row driven by a list of strings is how the
 * ESI box comes to write the PT column.
 *
 * "Others" is not here: it is a text box rather than a seventh flag, because
 * "Others" ticked with nothing written says only that *something* was excluded,
 * which the client reading the profile cannot act on.
 */
export interface ExclusionField {
  field:
  | 'excludesPf'
  | 'excludesEsi'
  | 'excludesPt'
  | 'excludesIncentives'
  | 'excludesPetrolAllowance'
  | 'excludesTaDa';
  label: string;
}

export const SALARY_EXCLUSIONS: ExclusionField[] = [
  { field: 'excludesPf', label: 'PF' },
  { field: 'excludesEsi', label: 'ESI' },
  { field: 'excludesPt', label: 'PT' },
  { field: 'excludesIncentives', label: 'Incentives' },
  { field: 'excludesPetrolAllowance', label: 'Petrol Allowance' },
  { field: 'excludesTaDa', label: 'TA & DA' },
];

/**
 * What the figure excludes, in one line, or null where nothing was said.
 *
 * Null rather than "Nothing" so every reader renders it as the same em dash it
 * renders an unanswered field with. Nobody ticked the boxes and the salary
 * being a gross figure are different claims, and the form cannot tell them
 * apart — so neither does this.
 */
export function exclusionSummary(
  candidate: Partial<Record<ExclusionField['field'], boolean>> & {
    excludesOther?: string | null;
  },
): string | null {
  const ticked = SALARY_EXCLUSIONS
    .filter((option) => candidate[option.field])
    .map((option) => option.label);
  const other = (candidate.excludesOther ?? '').trim();
  if (other) ticked.push(other);
  return ticked.length ? ticked.join(', ') : null;
}

/**
 * A date column as DD/MM/YYYY.
 *
 * The API sends ISO — "1994-04-17" — which sorts correctly and is read by
 * nobody in this office as anything but an American date with the year moved.
 * Every other date a recruiter types here is DD-MM-YYYY, and a screen that
 * shows one format and asks for another is how 04/17 gets entered as 17/04.
 *
 * Parsed by splitting rather than through `new Date`: a bare "1994-04-17" is
 * parsed as UTC midnight and printed in local time, which in any timezone west
 * of Greenwich prints the day before. This has no timezone to get wrong.
 */
export function formatDayMonthYear(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const [year, month, day] = iso.slice(0, 10).split('-');
  if (!year || !month || !day) return null;
  return `${day}/${month}/${year}`;
}

/**
 * Where the recruiter says a candidate has got to.
 *
 * The client's own status column, in the client's own words. Grouped in the
 * order things happen, because a flat list of seventeen is a list to read
 * rather than a stage to pick — and the four "why they stopped" values at the
 * end are not a later stage, they are an exit from any of them.
 *
 * Mirrors `CandidateInterviewStatus` in `app/models/enums.py`. The value is
 * the column's, the label is the sheet's: the server refuses anything not in
 * the enum, so a typo here is a 422 rather than a status nobody can filter on.
 *
 * **It drives nothing.** Picking a value creates no submission, no offer and
 * no placement. The Status column shows this in preference to the derived
 * pipeline state, because a person saying where somebody is outranks an
 * inference — but neither overwrites the other.
 */
export interface InterviewStatusChoice extends Choice {
  group: 'Interview' | 'Decision' | 'Offer' | 'Did not proceed';
}

export const INTERVIEW_STATUSES: InterviewStatusChoice[] = [
  { value: 'first_round', label: '1st Round', group: 'Interview' },
  { value: 'second_round', label: '2nd Round', group: 'Interview' },
  { value: 'final_round', label: 'Final Round', group: 'Interview' },
  { value: 'hr_round', label: 'HR Round', group: 'Interview' },

  { value: 'selected', label: 'Selected', group: 'Decision' },
  { value: 'salary_negotiation', label: 'Salary Negotiation', group: 'Decision' },
  { value: 'rejected', label: 'Rejected', group: 'Decision' },
  {
    value: 'documentation_pending',
    label: 'Documentation Pending',
    group: 'Decision',
  },

  { value: 'offer_letter_pending', label: 'Offer Letter Pending', group: 'Offer' },
  { value: 'offer_declined', label: 'Offer Declined', group: 'Offer' },
  { value: 'yet_to_join', label: 'Yet to Join', group: 'Offer' },
  { value: 'joined_working', label: 'Joined & Working', group: 'Offer' },

  { value: 'drop', label: 'Drop', group: 'Did not proceed' },
  {
    value: 'high_salary_expectation',
    label: 'High Salary Expectation',
    group: 'Did not proceed',
  },
  { value: 'location_issue', label: 'Location Issue', group: 'Did not proceed' },
  {
    value: 'duplicate_at_client',
    label: 'Duplicated at Client',
    group: 'Did not proceed',
  },
  { value: 'rehire_candidate', label: 'Rehire Candidate', group: 'Did not proceed' },
];

/** The groups, in order, for a form that renders optgroups. */
export const INTERVIEW_STATUS_GROUPS: InterviewStatusChoice['group'][] = [
  'Interview',
  'Decision',
  'Offer',
  'Did not proceed',
];

/**
 * The statuses that imply a joining date, so the form asks for one.
 *
 * Both of them, not just Yet to Join. Somebody recording a candidate who
 * started last week has a date too, and a form that only asked on the way
 * past would make it unrecordable for anybody entered late — which is most of
 * a backlog being caught up on.
 *
 * Mirrors JOINING_STATUSES in `app/models/enums.py`.
 */
export const JOINING_STATUSES = ['yet_to_join', 'joined_working'] as const;

export function asksForJoiningDate(status: string | null | undefined): boolean {
  return (JOINING_STATUSES as readonly string[]).includes(status ?? '');
}

/** The sheet's wording for a stored status, or null where none is set. */
export function interviewStatusLabel(status: string | null | undefined): string | null {
  if (!status) return null;
  const match = INTERVIEW_STATUSES.find((option) => option.value === status);
  // A value the list does not know rather than a blank cell: the column is
  // validated against the enum, so this can only happen if the enum grows
  // before this list does — and a readable fallback is how somebody notices.
  return match ? match.label : status.replace(/_/g, ' ');
}

/**
 * How the interview is being held.
 *
 * The same three the Interviews page offers, because they are the same column
 * — a walk-in is a face-to-face interview, which is why there is no fourth.
 * Mirrors `InterviewMode` in `app/models/enums.py`.
 */
export const INTERVIEW_MODES: Choice[] = [
  { value: 'telephonic', label: 'Telephonic' },
  { value: 'f2f', label: 'Face to Face' },
  { value: 'virtual', label: 'Virtual' },
];

/** A stored mode as a person reads it. */
export function interviewModeLabel(mode: string | null | undefined): string | null {
  if (!mode) return null;
  const match = INTERVIEW_MODES.find((option) => option.value === mode);
  return match ? match.label : mode.replace(/_/g, ' ');
}

/**
 * The recruiter-set statuses that put a candidate on the Joining Tracker.
 *
 * Mirrors `TRACKER_INTERVIEW_STATUSES` in `app/services/tracker.py`, which is
 * what the server actually enforces — this copy exists so the tracker's filter
 * offers only options that can match. Offering "1st Round" there would return
 * nothing every time, and a control that cannot match looks broken rather than
 * empty.
 *
 * The line is drawn where `ON_THE_TRACKER` draws it for the derived status:
 * everything from the client's yes onward. A candidate still being interviewed
 * belongs on Interview Candidates, and Rejected, Location Issue and the rest
 * have no joining date to track.
 */
export const TRACKER_INTERVIEW_STATUSES: string[] = [
  'selected',
  'salary_negotiation',
  'documentation_pending',
  'offer_letter_pending',
  'offer_declined',
  'yet_to_join',
  'joined_working',
  'drop',
];
